import React, { useRef, type PropsWithChildren } from 'react';

import { BasicInput } from '../BasicInput/BasicInput';
import { RootWrapper } from '../RootWrapper';
import type { NumberInputProps, WheelHandler } from '../types';
import { validateChildren } from '../utils';

import { shouldRenderUnits } from './helpers';
import styles from './NumberInput.module.css';
import { useBasicNumberInputProps } from './useBasicNumberInputProps';

/**
 * Provides a number input with optional embedded slot content and a unit.
 */
export const NumberInput = React.forwardRef<
  HTMLInputElement,
  PropsWithChildren<NumberInputProps>
>(({ children, ...props }, ref) => {
  validateChildren(children);
  const handleWheelRef = useRef<WheelHandler>(null);
  const basicInputProps = useBasicNumberInputProps(props, handleWheelRef);

  return (
    <RootWrapper {...props} handleWheelRef={handleWheelRef}>
      {shouldRenderUnits(props) ? (
        <span className={styles['number-input-wrapper']}>
          <BasicInput
            ref={ref}
            className={styles['number-input']}
            data-testid="numeric-input"
            {...basicInputProps}
          />
          <span className={styles['number-input-unit']}>{props.unit}</span>
        </span>
      ) : (
        <BasicInput
          ref={ref}
          className={styles['number-input']}
          data-testid="numeric-input"
          {...basicInputProps}
        />
      )}
      {children}
    </RootWrapper>
  );
});
NumberInput.displayName = 'Number';
